import camelcase from 'camelcase';
import { TOptions } from 'i18next';
import { curry, find, mergeDeepLeft, pick, reduce } from 'ramda';
import React, { ReactElement, useCallback, useMemo } from 'react';
import { Trans as LibTrans, TransProps, useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown/with-html';
import { useSelector } from 'react-redux';
import {
  isOptInOrOptOut,
  selectCreditApplication,
} from 'state/creditApplication';
import { selectInstantCredit } from 'state/instantCredit';
import { selectTenant, Tenant } from 'state/tenant';
import { fallBackForKey, mergeOverrides, Override } from './overrides';

/**
 * Similar signature to i18next.TFunction, but more limited.
 * This is sufficient for current usage.
 * May need some expansion for other form of usage.
 *
 * Particularly, if we want to support fallback keys array,
 * the fallBackForKey function needs to be expanded to account for that.
 */
interface NewTFunction {
  (key: string, options?: TOptions | string): string;
  (key: string, defaultValue?: string, options?: TOptions | string): string;
}

/**
 * Generate an override object that is turned on or off depending only on
 * the tenant overrideConfig.
 */
const getSimpleOverrides = curry(
  <T extends keyof Tenant['overrideConfig']>(
    overrideConfig: Tenant['overrideConfig'],
    precedence: readonly T[],
  ): Override<T> => {
    return {
      precedence: precedence,
      shouldApply: (pick(precedence as T[], overrideConfig) as unknown) as {
        [K in T]: T extends Exclude<string, T> ? boolean : boolean | undefined;
      },
    };
  },
);

const useOptIntOutOverrides = (): Override<'optIn' | 'optOut'> => {
  const tenant = useSelector(selectTenant);
  const creditApplication = useSelector(selectCreditApplication);
  const optInOut = isOptInOrOptOut(creditApplication, tenant);
  return useMemo(
    () => ({
      precedence: ['optIn', 'optOut'] as const,
      shouldApply: {
        optIn: optInOut === 'optIn',
        optOut: optInOut === 'optOut',
      },
    }),
    [optInOut],
  );
};

export const useProductChoiceOverrides = (): Override<string> => {
  const {
    enabledFeatures: { productChoices },
  } = useSelector(selectTenant);
  const { productChoiceKey } = useSelector(selectCreditApplication);
  return useMemo(() => {
    if (!productChoiceKey || !productChoices) {
      return { precedence: [], shouldApply: {} };
    }

    const overrideKey = `productChoices.${productChoiceKey}`;
    return {
      precedence: [overrideKey] as const,
      shouldApply: { [overrideKey]: true },
    };
  }, [productChoiceKey, productChoices]);
};

/**
 * TODO: Rename this, since this has little to do with tenantConfig
 * dspOverrides and it also applies to more than the DSP.
 */
export const useDspOverrides = () => {
  const { cardType: icCardType } = useSelector(selectInstantCredit);
  const { cardType: creditAppCardType } = useSelector(selectCreditApplication);
  return useMemo(() => {
    const cardType = icCardType || creditAppCardType;
    if (!cardType) {
      return { precedence: [], shouldApply: {} };
    }

    // Expect cardType translation key will also be camelcased like
    // tenant dsp overrides keys.
    const overrideKey = `dspOverrides.${camelcase(cardType)}`;
    return {
      precedence: [overrideKey] as const,
      shouldApply: { [overrideKey]: true },
    };
  }, [icCardType, creditAppCardType]);
};

const workflowPrecedence = [
  'phoneLookup',
  'tokenLookup',
  'prescreen',
  'batchPrescreen',
  'prequalPhoneLookup',
  'prequalTokenLookup',
  'prequalOfferPush',
  'dspPush',
  'enhancedCheckoutPhoneLookup',
  'enhancedCheckoutTokenLookup',
  'membershipValidation',
  'icDriverLicense',
  'prequalDriverLicense',
  'enhancedPresentment',
  'stage',
  'production',
] as const;

const featurePrecedence = [
  'employeeApplication',
  'clerkMode',
  'kioskMode',
  'inStore',
] as const;

const useOverrides = () => {
  const optInOutOverrides = useOptIntOutOverrides();
  const productChoiceOverrides = useProductChoiceOverrides();
  const dspOverrides = useDspOverrides();
  const { overrideConfig } = useSelector(selectTenant);

  return useMemo(() => {
    const simpleOverrideFor = getSimpleOverrides(overrideConfig);

    const featureOverrides = simpleOverrideFor(featurePrecedence);
    const workflowOverrides = simpleOverrideFor(workflowPrecedence);

    const result = mergeOverrides([
      featureOverrides,
      optInOutOverrides,
      productChoiceOverrides,
      dspOverrides,
      workflowOverrides,
    ]);
    return result;
  }, [overrideConfig, optInOutOverrides, productChoiceOverrides, dspOverrides]);
};

/**
 * Return a callback that find the true i18nKey that will be used given the
 * tenant override config. Return undefined if none are found.
 */
const useKeyResolution = () => {
  const overrides = useOverrides();

  const { i18n } = useTranslation();

  const resolveKey = useCallback(
    (i18nKey: string): string | undefined => {
      const fallbacks = fallBackForKey(i18nKey, overrides);
      const result = find(key => i18n.exists(key), fallbacks);
      return result;
    },
    [i18n, overrides],
  );

  return resolveKey;
};

const useTranslate = (): NewTFunction => {
  const { t, i18n } = useTranslation();
  const overrides = useOverrides();

  const getPlacements = useCallback(
    (placementKey: string, tOptions = {}) => {
      const getGlobals = (ns?: string) => {
        const fallbacks = fallBackForKey(placementKey, overrides);
        const merged = reduce(
          (acc, key) => {
            if (!i18n.exists(key)) {
              return acc;
            }
            return mergeDeepLeft(
              acc,
              t(key, { ...tOptions, returnObjects: true, ns }),
            );
          },
          {},
          fallbacks,
        );
        return merged;
      };

      const globalsForCurrentNs = getGlobals();
      const defaultGlobals = getGlobals('default');
      return mergeDeepLeft(globalsForCurrentNs, defaultGlobals);
    },
    [i18n, overrides, t],
  );

  const epPlacements = getPlacements('epPlacements');
  // ACHTUNG!: you MUST pass the "object-wrapped" key-value like below (e.g. `{ epPlacements }`)
  //  with the proper name for the variable that you want to use as a translation key
  //  i.e. the function expects { 'epPlacements': { [...epPlacements...] } }
  //       such that it can use `...tOptions` to spread the values under the key.
  // TL;DR - the `epPlacements` variable's name is "special" - don't change it arbitarily.
  const globals = getPlacements('globals', { epPlacements });

  const newTranslation: NewTFunction = useCallback(
    ((
      key: string,
      defaultValueOrOptions?: string | TOptions,
      options?: TOptions,
    ) => {
      let defaultValue: string | undefined;
      options = options || {};

      if (defaultValueOrOptions) {
        if (typeof defaultValueOrOptions !== 'string') {
          options = defaultValueOrOptions;
        } else {
          defaultValue = defaultValueOrOptions;
        }
      }
      // let the "globals" block be interpolated everywhere
      options = {
        globals,
        epPlacements,
        ...options,
      };

      const overrideString = fallBackForKey(key, overrides);

      return t(overrideString, defaultValue, options);
    }) as NewTFunction,
    [overrides, t, globals, epPlacements],
  );

  return newTranslation;
};

/**
 * Same as NewTFunction, but it renders Markdown from the translation value
 * into a ReactElement.
 */
interface TMarkdownFunction {
  (key: string, options?: TOptions | string): ReactElement;
  (
    key: string,
    defaultValue?: string,
    options?: TOptions | string,
  ): React.ReactElement;
}

const useMarkdownTranslate = (): TMarkdownFunction => {
  const t = useTranslate();

  const newTranslation: TMarkdownFunction = useCallback(
    ((...params: Parameters<TMarkdownFunction>) => {
      const children = t(...params);
      // Render paragraphs with <span>
      return (
        <ReactMarkdown
          renderers={{
            paragraph: 'span',
            link: props => (
              <a href={props.href} target="_blank" rel="noopener noreferrer">
                {props.children}
              </a>
            ),
          }}
          allowDangerousHtml
        >
          {children}
        </ReactMarkdown>
      );
    }) as TMarkdownFunction,
    [t],
  );

  return newTranslation;
};

const useMarkdownMultilineTranslate = (): TMarkdownFunction => {
  const t = useTranslate();

  const newTranslation: TMarkdownFunction = useCallback(
    ((...params: Parameters<TMarkdownFunction>) => {
      const children = t(...params);
      // Render paragraphs inheriting styling
      return (
        <ReactMarkdown
          renderers={{
            paragraph: props => <p {...props} style={{ all: 'inherit' }} />,
            link: props => (
              <a href={props.href} target="_blank" rel="noopener noreferrer">
                {props.children}
              </a>
            ),
          }}
          allowDangerousHtml
        >
          {children}
        </ReactMarkdown>
      );
    }) as TMarkdownFunction,
    [t],
  );

  return newTranslation;
};

const Trans: React.FC<TransProps> = ({ i18nKey, ...restProps }) => {
  const resolveKey = useKeyResolution();

  const key = useMemo(() => {
    if (i18nKey === undefined) {
      return i18nKey;
    }

    const result = resolveKey(i18nKey);

    if (result === undefined) {
      // If none of the keys exists, let i18n handles it to be consistent with
      // other missing keys situation.
      return i18nKey;
    }
    return result;
  }, [i18nKey, resolveKey]);

  return <LibTrans {...restProps} i18nKey={key} />;
};

export default useTranslate;
export {
  Trans,
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
  useKeyResolution,
};
